/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * WalletProvider component for managing the selected/active wallet
 */
import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { WalletContext, type WalletContextValue } from './wallet-context';

// Re-export types and context for backwards compatibility
export { WalletContext, type WalletContextValue } from './wallet-context';

export interface WalletProviderProps {
  children: ReactNode;
}

/**
 * WalletProvider manages the selected wallet state
 *
 * - Syncs with Privy's useWallets() hook
 * - Defaults to embedded Privy wallet (then first wallet) when no selection
 * - Falls back to embedded wallet if selected wallet disconnects
 */
export function WalletProvider({ children }: WalletProviderProps) {
  const { user } = usePrivy();
  const { ready, wallets } = useWallets();
  const [selectedAddress, setSelectedAddressState] = useState<string | null>(null);

  // Get the user's Privy wallet address - this is the authoritative source
  const privyWalletAddress = user?.wallet?.address ?? null;

  // Get default wallet - find wallet matching user's Privy wallet address
  // This ensures we always use the correct wallet, not just any Privy wallet
  const defaultWallet = useMemo(() => {
    if (privyWalletAddress) {
      const matchingWallet = wallets.find(
        (w) => w.address.toLowerCase() === privyWalletAddress.toLowerCase()
      );
      if (matchingWallet) return matchingWallet;
    }
    // Fallback to first Privy wallet or first wallet
    const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
    return embeddedWallet ?? wallets[0] ?? null;
  }, [wallets, privyWalletAddress]);

  // Find the selected wallet object
  const selectedWallet = useMemo(() => {
    if (!selectedAddress) return defaultWallet;
    return wallets.find((w) => w.address.toLowerCase() === selectedAddress.toLowerCase()) ?? null;
  }, [wallets, selectedAddress, defaultWallet]);

  // Effective address (selected or fallback to embedded wallet)
  const effectiveAddress = selectedWallet?.address ?? defaultWallet?.address ?? null;

  // Sync selected address when wallets change
  useEffect(() => {
    if (!ready) return;

    // If no wallets connected, clear selection
    if (wallets.length === 0) {
      setSelectedAddressState(null);
      return;
    }

    // Only auto-select on initial load (no explicit selection yet)
    if (!selectedAddress) {
      setSelectedAddressState(defaultWallet?.address ?? null);
    }
  }, [ready, wallets, selectedAddress, defaultWallet]);

  // Set selected address (purely local state — no wallet interactions triggered)
  const setSelectedAddress = useCallback((address: string) => {
    setSelectedAddressState(address);
  }, []);

  const value = useMemo<WalletContextValue>(
    () => ({
      selectedAddress: effectiveAddress,
      setSelectedAddress,
      wallets,
      selectedWallet,
      isReady: ready,
    }),
    [effectiveAddress, setSelectedAddress, wallets, selectedWallet, ready]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
